<template>
  <div class="flex flex-col w-full">
    <Toast position="top-[48px]"></Toast>
    <div class="text-text-display-lg font-black text-brown-800 tracking-[-0.03em]">
      Help us confirm it's really you.
    </div>
    <div class="text-text-body-md text-brown-800 font-semibold pt-[8px]">
      Please enter the 6-digit code sent to <br />
      <span class="font-black text-grey-100 tracking-[-0.03em]">{{ email }}</span>
    </div>

    <div class="pt-[40px]">
      <div class="w-full flex justify-between mb-[16px]">
        <v-otp-input
          ref="otpInput"
          v-model:value="otpValue"
          :input-classes="
            isMaxAttemptsReached ? 'otp-input-disabled' : hasError ? 'otp-input-error' : 'otp-input'
          "
          input-type="number"
          inputmode="numeric"
          separator=" "
          :num-inputs="6"
          :should-auto-focus="true"
          :is-disabled="loading || isMaxAttemptsReached"
          @on-complete="handleNext"
        />
      </div>

      <p v-if="errorMessage" class="text-text-body font-semibold text-status-error mt-[8px]">
        {{ errorMessage }}
      </p>

      <p
        class="text-text-body-md mt-[32px] mb-[36px] text-grey-900 text-center"
        v-if="!showResendButton"
      >
        Resend Code in {{ showResendButtonCount }} sec
      </p>

      <div
        class="mt-[32px] mb-[36px] flex justify-center"
        v-if="showResendButton && !isMaxAttemptsReached"
      >
        <DsLink underline="always" :disabled="isResending" @click="sendOtp"> Resend Code </DsLink>
      </div>
    </div>

    <div
      class="absolute inset-x-0 bottom-0 px-[24px] md:px-0 mb-[32px]"
      :style="hasFocusedField ? `bottom: ${keyboardInsetCss}` : ''"
    >
      <BottomButton
        text="Next"
        :disabled="loading || !otpValue || otpValue.length !== 6 || isMaxAttemptsReached"
        @click="handleNext"
        class="w-full md:max-w-[552px] md:mx-auto"
      />
    </div>
  </div>
</template>

<script>
import { OTP_RESEND_TIME, ONE_SEC, THREE_SEC } from '@/constants/registration-endpoints';
import { Link as DsLink } from '@team-hogc/hogc-one-design-system';
import VOtpInput from 'vue3-otp-input';
import Toast from '@/components/common/Toast.vue';
import BottomButton from '@/components/common/BottomButton.vue';
import { mapActions, mapState } from 'vuex';
import keyboardFieldIntoView from '@/mixins/keyboard-field-into-view';

export default {
  mixins: [keyboardFieldIntoView],
  name: 'MobileOtp',
  components: {
    DsLink,
    VOtpInput,
    Toast,
    BottomButton,
  },
  props: ['isOtpType', 'email', 'registrationPageData'],
  data() {
    return {
      isVisibleAlert: false,
      alertContent: '',
      otpValue: '',
      showResendButton: false,
      showResendButtonCount: OTP_RESEND_TIME,
      otpTimer: null,
      attemptCount: 0,
      maxAttempts: 3,
      errorMessage: '',
      hasError: false,
      isResending: false,
    };
  },
  computed: {
    ...mapState('registration', {
      loading: 'loading',
    }),
    isMaxAttemptsReached() {
      return this.attemptCount >= this.maxAttempts;
    },
  },
  watch: {
    otpValue(newValue) {
      if (newValue && this.hasError) {
        this.hasError = false;
        this.errorMessage = '';
      }
    },
  },
  mounted() {
    this.attemptCount = 0;
    this.hasError = false;
    this.errorMessage = '';
    this.otpValue = '';
    if (this.$refs.otpInput) {
      this.$refs.otpInput.clearInput();
    }
    this.startResendTimer();

    this.startKeyboardOverlapTracking();
  },
  beforeUnmount() {
    if (this.otpTimer) {
      clearInterval(this.otpTimer);
    }

    this.attemptCount = 0;
    this.hasError = false;
    this.errorMessage = '';
    this.otpValue = '';
    if (this.$refs.otpInput) {
      this.$refs.otpInput.clearInput();
    }
  },
  methods: {
    ...mapActions('registration', ['sendEmailOtp', 'verifyEmailOtp']),
    startResendTimer() {
      this.showResendButton = false;
      this.showResendButtonCount = OTP_RESEND_TIME;

      this.otpTimer = setInterval(() => {
        this.showResendButtonCount--;
        if (this.showResendButtonCount === 0) {
          this.showResendButton = true;
          clearInterval(this.otpTimer);
        }
      }, ONE_SEC);
    },
    async sendOtp() {
      if (this.isResending) return;
      this.isResending = true;
      try {
        this.startResendTimer();
        await this.sendEmailOtp(this.email);

        this.attemptCount = 0;
        this.hasError = false;
        this.errorMessage = '';
      } catch (error) {
        this.renderAlertEl({
          type: 'error',
          message: error.message,
        });
      } finally {
        this.isResending = false;
      }
    },
    onCloseToastShow() {
      this.isVisibleAlert = false;
      this.alertContent = '';
    },
    renderAlertEl(config) {
      const { type, message } = config;
      this.alertContent = message;
      this.isVisibleAlert = true;
      setTimeout(() => {
        this.isVisibleAlert = false;
        this.alertContent = '';
      }, THREE_SEC);
    },
    async handleNext() {
      if (this.isMaxAttemptsReached) {
        return;
      }

      try {
        const result = await this.verifyEmailOtp({
          email: this.email,
          otp: this.otpValue,
        });

        if (result.success) {
          this.attemptCount = 0;
          this.hasError = false;
          this.errorMessage = '';
          this.$emit('next', true);
        } else {
          this.attemptCount++;
          this.hasError = true;

          // Clear OTP input
          this.$refs.otpInput.clearInput();
          this.otpValue = '';

          if (this.isMaxAttemptsReached) {
            this.errorMessage = 'You have reached the maximum attempt allowed.';
          } else {
            this.errorMessage = 'Wrong OTP entered. Try again.';
          }

          this.renderAlertEl({
            type: 'error',
            message: this.errorMessage,
          });
        }
      } catch (error) {
        this.attemptCount++;
        this.hasError = true;

        this.$refs.otpInput.clearInput();
        this.otpValue = '';

        if (this.isMaxAttemptsReached) {
          this.errorMessage = 'You have reached the maximum attempt allowed.';
        } else {
          this.errorMessage = 'Wrong OTP entered. Try again.';
        }

        this.renderAlertEl({
          type: 'error',
          message: error.message || 'An error occurred',
        });
      }
    },
  },
  beforeMount() {
    if (
      this.registrationPageData.isOtpVerified &&
      !this.registrationPageData.aboutyourself.isEmailUpdated
    ) {
      this.$emit('nextButtonHandler', true);
    }
  },
};
</script>

<style scoped>
:deep(.otp-input) {
  width: 48px;
  height: 69px;
  border-radius: 12px;
  border: 1px solid #d8dadc;
  text-align: center;
  background: #f6f4f0;
  font-size: 18px;
  font-weight: 600;
}

:deep(.otp-input:focus) {
  border: 1.1px solid #454bef;
  outline: none;
}

:deep(input::-webkit-outer-spin-button),
:deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

:deep(input[type='number']) {
  -moz-appearance: textfield;
  appearance: textfield;
}

:deep(.otp-input-error) {
  width: 48px;
  height: 69px;
  border-radius: 12px;
  text-align: center;
  background: #f6f4f0;
  font-size: 18px;
  font-weight: 600;
  border: 1.5px solid #ef4444 !important;
}

:deep(.otp-input-error:focus) {
  border: 1.5px solid #ef4444 !important;
  outline: none;
}

:deep(.otp-input-disabled) {
  width: 48px;
  height: 69px;
  border-radius: 12px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  background: #e5e7eb !important;
  border: 1px solid #d1d5db !important;
  cursor: not-allowed !important;
  color: #9ca3af !important;
}

:deep(.otp-input-disabled:focus) {
  background: #e5e7eb !important;
  border: 1px solid #d1d5db !important;
  outline: none;
}
</style>
